import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { legalStatusValue } from '@/lib/domain/legalStatus';
import { DEALS_QUERY_KEY, patchDealInCaches, restoreDealItem, snapshotDealItem } from './useDeals';
import { type ApiDealResponse, readApiETag } from './types';

/**
 * Liga/desliga o "prioritário" de um deal. Não há comando dedicado no backend — prioritário é
 * campo normal do UpdateDeal —, então: GET do deal (pega ids de tese/equipe/tags + ETag) → PUT
 * com IsPriority invertido e o RESTO idêntico (o handler faz diff → sem histórico falso).
 * Otimista; 412 → rollback + refetch. LegalStatus volta do GET como string e é reenviado como
 * número (bind de enum por número).
 *
 * Follow-up: um POST /api/deals/{id}/priority dedicado (espelhando o de fase) eliminaria o GET+PUT.
 */
export function useToggleDealPriority() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const deal = await apiFetch<ApiDealResponse>(`/api/deals/${id}`);
      return apiFetch<ApiDealResponse>(`/api/deals/${id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${readApiETag(deal)}"` },
        body: {
          codename: deal.codename,
          legalStatus: legalStatusValue(deal.legalStatus),
          folderId: deal.folderId,
          targetCompanyId: deal.targetCompanyId,
          originatorIdSnapshot: deal.originatorIdSnapshot,
          originatorPersonIdSnapshot: deal.originatorPersonIdSnapshot,
          internalOriginatorId: deal.internalOriginatorId,
          leadUserId: deal.leadUserId,
          investmentValueBRL: deal.investmentValueBRL,
          thesisSummary: deal.thesisSummary,
          isPriority: !deal.isPriority,
          thesisIds: deal.thesisIds,
          teamUserIds: deal.teamUserIds,
          tags: deal.tags,
        },
      });
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: DEALS_QUERY_KEY });
      const prev = snapshotDealItem(qc, id);
      patchDealInCaches(qc, id, (it) => ({ ...it, isPriority: !it.isPriority }));
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) restoreDealItem(qc, vars.id, ctx.prev);
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa. Atualizei a lista.', 'warn');
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível atualizar o prioritário.', 'danger');
      }
    },
    onSuccess: (resp) => {
      patchDealInCaches(qc, resp.id, (it) => ({
        ...it,
        isPriority: resp.isPriority,
        eTag: readApiETag(resp) || it.eTag,
      }));
    },
  });
}
